<script lang="ts">
	//Svelte
	import { onDestroy, onMount } from 'svelte';
	//Services
	import { format } from 'svelte-i18n';
	//Design system components
	import {
		DropdownMenu,
		MenuItemList,
		IconButton,
		Modal,
		Text,
		SelectCard,
		CardGrid,
	} from '@Shelly/shelly-design-system/index';
	//Helpers
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';
	import { generateCSVStatistic } from './chart_control_helpers';
	import { CHART_VARIANTS_LIST, updateDeviceInterfaceData } from './chart_helper';
	import { chartMoreActionsGroupedItems, setSelectedItemID } from './chart_more_actions_helper';
	//Types
	import type { ChartMoreActionType } from './chart_more_actions_helper';
	import type { ChartConfig, ChartDataInterface, ChartStatisticInterface } from 'src/types/Chart';
	import DeviceChart from './DeviceChart.svelte';
	import type { Device } from 'src/types/Device';
	import { debounce } from 'src/helpers/utilities';
	import { isThreeEM } from 'src/helpers/interface_helpers';
	import { SystemScreenBreakpoints } from '@Shelly/shelly-design-system/helpers/enums';
	import { mapIconSolidWeightToLight } from 'src/helpers/new-layout-helpers';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	let className = '';
	export { className as class };
	export let fullScreen = false;
	export let fullscreenTarget: HTMLElement = null;
	export let device: Device;
	export let statsData: Promise<{
		chartData: ChartDataInterface;
		chartStatistic: ChartStatisticInterface;
	}>;
	export let chartElement: DeviceChart;
	export let chartConfig: ChartConfig;
	export let chartTime: { from: Date; to: Date };
	export let dummyNetMetering = false;
	export let onNetMeteringToggle: () => void = null;

	let fullscreenBox: HTMLElement;
	let showDropdownMenuGrouped = false;
	let showModalMenuGrouped = false;
	let selectedItemID: string | number;
	let windowClientWidth: number;

	const onToggleMoreActions = () => {
		if (!isMobile && !isTablet) {
			showDropdownMenuGrouped = !showDropdownMenuGrouped;
		} else {
			showModalMenuGrouped = !showModalMenuGrouped;
		}
	};

	const onCloseShowMenuGrouped = () => {
		showModalMenuGrouped = false;
	};

	const changeChartType = async (type: string) => {
		if (!chartElement) return;

		const foundTypeIndex = CHART_VARIANTS_LIST.findIndex((t) => t === type);
		const currentTypeIndex = foundTypeIndex !== -1 ? foundTypeIndex : null;
		const typeIndex = chartElement.getChartUtil().changeType(currentTypeIndex);
		chartConfig.type = CHART_VARIANTS_LIST[typeIndex];

		if (!device?.shared) {
			device[`${chartConfig.id}_type`] = typeIndex;
			debounce(() => updateDeviceInterfaceData(device), 500);
		}
	};

	const onExportFile = async () => {
		try {
			const deviceName = device?.name;
			const result = await statsData;
			generateCSVStatistic(result.chartData, deviceName, chartTime);
		} catch (error) {
			// faild to get statistic data
		}
	};

	const onActionClick = async (action: ChartMoreActionType) => {
		const id = action.id;
		if (action.isSelectable) {
			selectedItemID = id;
		}
		onToggleMoreActions();

		if (id === 'fullScreen') {
			toggleFullScreen();
		} else if (id === 'exportFile') {
			await onExportFile();
		} else if (id === 'netMetering') {
			if (onNetMeteringToggle) {
				onNetMeteringToggle();
			}
		} else {
			await changeChartType(id);
		}
	};

	const toggleFullScreen = () => {
		fullScreen = !fullScreen;
		if (fullScreen) {
			document.body.appendChild(fullscreenTarget);
		} else {
			fullscreenBox.appendChild(fullscreenTarget);
		}
	};

	const handleKeydown = (e: KeyboardEvent) => {
		if (fullScreen && e.key == 'Escape') {
			e.stopPropagation();
			toggleFullScreen();
		}
	};

	onMount(() => {
		document.addEventListener('keydown', handleKeydown, true);
		selectedItemID = setSelectedItemID(chartConfig);
	});

	onDestroy(() => {
		document.removeEventListener('keydown', handleKeydown, true);
		if (fullscreenTarget && fullscreenTarget.parentNode) {
			fullscreenTarget.parentNode.removeChild(fullscreenTarget);
		}
	});

	$: isMobile = windowClientWidth < SystemScreenBreakpoints.MOBILE_MAX;
	$: isTablet = windowClientWidth <= SystemScreenBreakpoints.TABLET_MAX;
</script>

<svelte:window bind:innerWidth={windowClientWidth} />

<div bind:this={fullscreenBox}>
	<div
		bind:this={fullscreenTarget}
		{...$$restProps}
		class={classMap({
			[className]: true,
			'fullscreen-container': true,
			fullScreen: fullScreen,
		})}
		style="user-select: none;
		background: {$showNewLayout ? 'var(--color-0)' : 'var(--color-2)'};"
	>
		<div class="title">
			<slot name="title" />
			<DropdownMenu class="expand-settings-btn" bind:show={showDropdownMenuGrouped}>
				<IconButton
					slot="trigger"
					aria-label={$format('title_more', { default: 'More' })}
					icon="fas fa-ellipsis-vertical"
					variant="secondary"
					on:click={onToggleMoreActions}
				/>
				{#each chartMoreActionsGroupedItems as group}
					<MenuItemList
						title={$format(group.groupTitle.translation, {
							default: group.groupTitle.value,
						})}
						isGroupTitle={group.isGroupTitle}
						isSelectable={false}
					/>
					{#each group.items as action}
						{@const isThreeEMTriphase =
							isThreeEM(device?.type) && device?.mode != 'monophase'}
						{@const shouldShowNetMetering = action.id === 'netMetering' && isThreeEMTriphase}
						{@const shouldShowAction =
							((fullScreen && action.id !== 'fullScreen') || !fullScreen) &&
							(shouldShowNetMetering || action.id !== 'netMetering')}
						{@const netMeteringTitle =
							action.id === 'netMetering'
								? dummyNetMetering
									? $format('exclude', { default: 'Exclude' }) +
										' ' +
										$format(action.title.translation, {
											default: action.title.value,
										})
									: $format('include', { default: 'Include' }) +
										' ' +
										$format(action.title.translation, {
											default: action.title.value,
										})
								: $format(action.title.translation, { default: action.title.value })}
						{#if shouldShowAction}
							<MenuItemList
								isGroupItem
								title={netMeteringTitle}
								subtitle={action.subtitle}
								selected={selectedItemID === action.id}
								isSelectable={action.isSelectable}
								icon={action.icon}
								on:click={() => {
									onActionClick(action);
								}}
							/>
						{/if}
					{/each}
				{/each}
			</DropdownMenu>

			{#if fullScreen}
				<IconButton
					aria-label={$format('toggle_fullscreen', {
						default: 'Toggle fullscreen',
					})}
					class="expand-btn"
					icon={`fas fa-down-left-and-up-right-to-center`}
					variant="secondary"
					on:click={toggleFullScreen}
				/>
			{/if}
		</div>
		<div class="content">
			<slot />
		</div>
	</div>
</div>

<Modal bind:showModal={showModalMenuGrouped} hideTitle on:close={onCloseShowMenuGrouped} zIndex={102}>
	<svelte:fragment slot="content">
		{#each chartMoreActionsGroupedItems as group, index}
			<Text styleType="label-large" class="mb-s">
				{$format(group.groupTitle.translation, {
					default: group.groupTitle.value,
				})}
			</Text>
			<CardGrid md={250} noPaddingBottom gridGap="s">
				{#each group.items as action}
					{@const isThreeEMTriphase = isThreeEM(device?.type) && device?.mode != 'monophase'}
					{@const shouldShowNetMetering = action.id === 'netMetering' && isThreeEMTriphase}
					{@const shouldShowAction =
						((fullScreen && action.id !== 'fullScreen') || !fullScreen) &&
						(shouldShowNetMetering || action.id !== 'netMetering')}
					{@const netMeteringTitle =
						action.id === 'netMetering'
							? dummyNetMetering
								? $format('exclude', { default: 'Exclude' }) +
									' ' +
									$format(action.title.translation, { default: action.title.value })
								: $format('include', { default: 'Include' }) +
									' ' +
									$format(action.title.translation, { default: action.title.value })
							: $format(action.title.translation, { default: action.title.value })}
					{#if shouldShowAction}
						<SelectCard
							cardBgColor={$showNewLayout ? 'color-1' : undefined}
							hideAction
							class="width-full"
							type="horizontal"
							title={netMeteringTitle}
							selected={selectedItemID === action.id}
							icon={mapIconSolidWeightToLight(action.icon)}
							on:click={() => {
								onActionClick(action);
							}}
						/>
					{/if}
				{/each}
			</CardGrid>
			{#if index < chartMoreActionsGroupedItems.length - 1}
				<hr class="mb-l mt-l" />
			{/if}
		{/each}
	</svelte:fragment>
</Modal>

<style>
	.fullscreen-container {
		position: relative;
		background: inherit;
		border-radius: var(--radius-big);
		padding: 8px;
	}
	.fullscreen-container.fullScreen {
		position: fixed;
		left: 0;
		top: 0;
		height: 100%;
		width: 100%;
		z-index: 100;
		border-radius: 0;
		overflow-x: hidden;
		overflow-y: auto;
	}
	.fullscreen-container .title {
		display: -webkit-box;
		display: -ms-flexbox;
		display: flex;
		-webkit-box-align: center;
		-ms-flex-align: center;
		align-items: center;
		-webkit-box-pack: center;
		-ms-flex-pack: center;
		justify-content: center;
		position: relative;
		padding-right: 36px;
		margin-bottom: 8px;
		height: 32px;
		width: 100%;
	}
	.fullscreen-container .title :global(.expand-btn) {
		position: absolute;
		top: 0px;
		right: 0px;
	}

	.fullscreen-container.fullScreen .title {
		padding-right: 76px;
	}

	.fullscreen-container .title :global(.expand-settings-btn) {
		position: absolute;
		top: 0px;
		right: 0px;
	}
	.fullscreen-container.fullScreen .title :global(.expand-settings-btn) {
		right: 40px;
	}
</style>
